import React, { useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../api';
import { useAsync } from '../../hooks/useAsync';
import type { Company, FundraisingStage } from '../../types';
import { companyTypeLabel, fundraisingStageLabel, probabilityLabel } from '../../i18n';
import { useToast } from '../../components/ToastHost';

const STAGES: FundraisingStage[] = [
  'Mapeados',
  'Contactados',
  'Envío Info Principal',
  '1ra Reunión',
  'Interesados en continuar',
  'Envio Data Room',
  'En Negociación',
  'En Term Sheet',
  'Ganado',
  'Proceso Abandonado',
];

type KanbanCompany = Company & {
  primaryContactName?: string | null;
  lastTouchAt?: string | null;
  lastTouchKind?: 'nota' | 'ingreso' | null;
};

function formatShortMoney(amount: number, currency: string) {
  if (!Number.isFinite(amount)) return '—';
  const abs = Math.abs(amount);
  const unit = abs >= 1_000_000 ? 'M' : abs >= 1_000 ? 'K' : '';
  const div = abs >= 1_000_000 ? 1_000_000 : abs >= 1_000 ? 1_000 : 1;
  const v = amount / div;
  const shown = abs >= 1_000 ? v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2) : String(Math.round(v));
  return `${currency} ${shown}${unit}`;
}

function expectedTicket(c: Company): number | null {
  if (c.ticketExact != null && Number.isFinite(c.ticketExact)) return c.ticketExact;
  if (c.ticketMin != null && c.ticketMax != null && Number.isFinite(c.ticketMin) && Number.isFinite(c.ticketMax)) {
    return (c.ticketMin + c.ticketMax) / 2;
  }
  if (c.ticketMin != null && Number.isFinite(c.ticketMin)) return c.ticketMin;
  if (c.ticketMax != null && Number.isFinite(c.ticketMax)) return c.ticketMax;
  return null;
}

export default function AdminKanban() {
  const { data, loading, error, setData } = useAsync<{ companies: KanbanCompany[] }>(() => apiGet('/api/admin/companies'), []);
  const toast = useToast();
  const [inactiveDays, setInactiveDays] = useState<number>(0);
  const [leadsRoundFilter, setLeadsRoundFilter] = useState<'all' | 'yes' | 'no'>('all');

  const filteredCompanies = useMemo(() => {
    const list = data?.companies || [];
    const cutoff = inactiveDays ? Date.now() - inactiveDays * 86400000 : null;
    return list.filter((c) => {
      if (cutoff != null) {
        if (!c.lastTouchAt) return true;
        const ts = Date.parse(c.lastTouchAt);
        if (!Number.isFinite(ts)) return true;
        if (ts > cutoff) return false;
      }
      if (leadsRoundFilter === 'yes') return Boolean(c.leadsRound);
      if (leadsRoundFilter === 'no') return !c.leadsRound;
      return true;
    });
  }, [data, inactiveDays, leadsRoundFilter]);

  const byStage = useMemo(() => {
    const map: Record<string, KanbanCompany[]> = {};
    STAGES.forEach((s) => (map[s] = []));
    (filteredCompanies || []).forEach((c) => {
      map[c.stage] = [...(map[c.stage] || []), c];
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')));
    return map;
  }, [filteredCompanies]);

  const move = async (companyId: string, stage: FundraisingStage) => {
    try {
      await apiPost(`/api/admin/companies/${companyId}/stage`, { stage });
      const updated = await apiGet<{ companies: KanbanCompany[] }>('/api/admin/companies');
      setData(updated);
      toast.success('Stage updated.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not update stage.');
      throw e;
    }
  };

  const totalsByStage = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    STAGES.forEach((s) => (out[s] = {}));
    (filteredCompanies || []).forEach((c) => {
      const t = expectedTicket(c);
      if (t == null) return;
      const cur = (c.currency || 'USD').toUpperCase();
      out[c.stage][cur] = (out[c.stage][cur] || 0) + t;
    });
    return out;
  }, [filteredCompanies]);

  const totalsLabel = (stage: FundraisingStage) => {
    const m = totalsByStage[stage] || {};
    const entries = Object.entries(m).sort((a, b) => b[1] - a[1]);
    if (!entries.length) return '—';
    const shown = entries.slice(0, 2).map(([cur, v]) => formatShortMoney(v, cur));
    const more = entries.length > 2 ? ` +${entries.length - 2}` : '';
    return shown.join(' · ') + more;
  };

  if (loading) return <div className="card">Loading Kanban…</div>;
  if (error) return <div className="card">Error: {error} (check Basic Auth)</div>;

  return (
    <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Fundraising Kanban</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Drag & drop between stages
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Inactivity
            </div>
            <select className="select" value={inactiveDays} onChange={(e) => setInactiveDays(parseInt(e.target.value, 10) || 0)} style={{ width: 180 }}>
              <option value={0}>All</option>
              <option value={7}>7+ days</option>
              <option value={14}>14+ days</option>
              <option value={30}>30+ days</option>
              <option value={60}>60+ days</option>
              <option value={90}>90+ days</option>
            </select>
            <div className="muted" style={{ fontSize: 12 }}>
              Lidera ronda
            </div>
            <select className="select" value={leadsRoundFilter} onChange={(e) => setLeadsRoundFilter(e.target.value as any)} style={{ width: 180 }}>
              <option value="all">Todos</option>
              <option value="yes">Sí</option>
              <option value="no">No</option>
            </select>
            <a className="btn" href="/admin/companies">
              Create/edit investors
            </a>
          </div>
        </div>
      <div className="kanban">
        {STAGES.map((s) => (
          <div
            key={s}
            className="col"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const companyId = e.dataTransfer.getData('text/companyId');
              if (!companyId) return;
              move(companyId, s).catch(() => {});
            }}
          >
            <div className="col-title">
              {fundraisingStageLabel(s)} <span className="pill">{(byStage[s] || []).length}</span>
            </div>
            <div className="col-body">
              {(byStage[s] || []).map((c) => (
                <div
                  key={c.id}
                  className="card"
                  style={{ margin: 0, cursor: 'grab' }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/companyId', c.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', marginBottom: 8 }}>
                    <div style={{ fontWeight: 900 }}>{c.name}</div>
                    {c.leadsRound ? (
                      <span className="pill" title="Lidera la ronda">
                        Lidera
                      </span>
                    ) : null}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    {companyTypeLabel(c.type)} · {c.country || '—'} · Prob: {probabilityLabel(c.probability)}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    {(() => {
                      const exact = c.ticketExact;
                      const min = c.ticketMin;
                      const max = c.ticketMax;
                      const cur = (c.currency || 'USD').toUpperCase();
                      if (exact != null) return `Ticket: ${formatShortMoney(exact, cur)}`;
                      if (min != null && max != null) {
                        const avg = (min + max) / 2;
                        return `Range: ${formatShortMoney(min, cur)}–${formatShortMoney(max, cur)} (avg ${formatShortMoney(avg, cur)})`;
                      }
                      const t = expectedTicket(c);
                      return t != null ? `Ticket: ${formatShortMoney(t, cur)}` : 'Ticket: —';
                    })()}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    Primary contact: {c.primaryContactName || '—'}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    Last activity: {c.lastTouchAt ? c.lastTouchAt.slice(0, 10) : '—'}{' '}
                    {c.lastTouchKind ? `(${c.lastTouchKind === 'nota' ? 'note' : c.lastTouchKind === 'ingreso' ? 'login' : c.lastTouchKind})` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="select" value={c.stage} onChange={(e) => move(c.id, e.target.value as FundraisingStage)}>
                      {STAGES.map((st) => (
                        <option key={st} value={st}>
                          {fundraisingStageLabel(st)}
                        </option>
                      ))}
                    </select>
                    <a className="btn" href={`/admin/companies/${c.id}`}>
                      Open
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="col-footer">
              <span className="muted" style={{ fontSize: 12 }}>
                Total ticket:
              </span>{' '}
              <span className="pill" title="Estimated total ticket (avg used when a range is provided)">
                {totalsLabel(s)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
